// ---------------------------------------------------------------------------
// Global instrumentation layer.
//
// Every HTTP call the app makes — Supabase REST/RPC/Auth/Storage, TanStack
// server functions under /_serverFn, server routes under /api, and any raw
// fetch left in a page — passes through window.fetch. Wrapping it once here
// means no page can issue an un-instrumented request, without having to
// rewrite thirty call sites.
//
// Rules:
//  - never throws into the caller; on any internal failure we fall through to
//    the original fetch;
//  - never records request bodies;
//  - never records the diagnostics writes themselves (infinite-loop guard);
//  - URLs are redacted before they are stored.
// ---------------------------------------------------------------------------

import { classifyHttpStatus, classifyNetworkError, classifySupabaseError } from "./classify";
import { CORRELATION_HEADER, currentCorrelationId, recordStep } from "./correlation";
import { newRequestId } from "./ids";
import { redactUrl } from "./redact";
import { recordPerf, report } from "./report";
import { DIAG_CODES, type DiagCategory, type PerfMetricName } from "./types";

let installed = false;

function urlOf(input: RequestInfo | URL): string {
  try {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    return (input as Request).url;
  } catch {
    return "";
  }
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  const m = init?.method ?? (typeof input === "object" && "method" in (input as any) ? (input as any).method : "GET");
  return String(m ?? "GET").toUpperCase();
}

/** Diagnostics' own writes must never be instrumented. */
function isSelfWrite(url: string): boolean {
  return url.includes("diagnostics_events") || url.includes("perf_metrics") || url.includes("diagnostics_alerts");
}

/** Maps an outgoing URL onto a diagnostics category + a coarse stage name. */
export function categorizeUrl(url: string): { category: DiagCategory; stage: string } {
  if (url.includes("/auth/v1/")) return { category: "auth", stage: "supabase_auth" };
  if (url.includes("/storage/v1/")) return { category: "upload", stage: "supabase_storage" };
  if (url.includes("/rest/v1/rpc/")) {
    const fn = url.split("/rest/v1/rpc/")[1]?.split(/[?#]/)[0] ?? "rpc";
    if (/bet/.test(fn)) return { category: "bet", stage: `rpc:${fn}` };
    if (/wallet|deposit|withdraw|adjust/.test(fn)) return { category: "wallet", stage: `rpc:${fn}` };
    if (/result|settle|publish/.test(fn)) return { category: "result", stage: `rpc:${fn}` };
    if (/fcm|push|notif/.test(fn)) return { category: "notification", stage: `rpc:${fn}` };
    return { category: "database", stage: `rpc:${fn}` };
  }
  if (url.includes("/rest/v1/")) {
    const table = url.split("/rest/v1/")[1]?.split(/[?#]/)[0] ?? "table";
    if (/^bets|bet_requests/.test(table)) return { category: "bet", stage: `table:${table}` };
    if (/wallet|deposit|withdraw/.test(table)) return { category: "wallet", stage: `table:${table}` };
    if (/result/.test(table)) return { category: "result", stage: `table:${table}` };
    if (/device_token|notification/.test(table)) return { category: "notification", stage: `table:${table}` };
    return { category: "database", stage: `table:${table}` };
  }
  if (url.includes("/_serverFn")) return { category: "api", stage: "server_function" };
  if (url.includes("/api/")) return { category: "api", stage: "server_route" };
  return { category: "network", stage: "http" };
}

/**
 * Wraps window.fetch so every request in the app is timed, classified and
 * reported. Install once, as early as possible on the client — after the
 * native origin proxy so we observe the final resolved URL.
 */
export function installDiagnosticsFetchInstrumentation(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = urlOf(input);
    if (!url || isSelfWrite(url)) return original(input as any, init);

    const started = Date.now();
    const requestId = newRequestId();
    const method = methodOf(input, init);
    const { category, stage } = categorizeUrl(url);

    // Propagate one correlation ID through client -> server -> provider so the
    // admin can replay the whole safe timeline for a single user action.
    const correlationId = currentCorrelationId();
    let finalInit = init;
    if (correlationId) {
      try {
        const headers = new Headers(
          init?.headers ?? (typeof input === "object" && "headers" in (input as any) ? (input as any).headers : undefined),
        );
        headers.set(CORRELATION_HEADER, correlationId);
        finalInit = { ...(init ?? {}), headers };
        recordStep({
          correlation_id: correlationId,
          step: "request_sent",
          code: requestId,
          detail: `${method} ${redactUrl(url)}`,
          metadata: { method, final_url: redactUrl(url), stage },
        });
      } catch {
        finalInit = init;
      }
    }

    try {
      const res = await original(input as any, finalInit);
      const duration_ms = Date.now() - started;
      const contentType = res.headers?.get?.("content-type") ?? null;
      const contentLength = res.headers?.get?.("content-length") ?? null;
      const deploymentId =
        res.headers?.get?.("x-deployment-id") ?? res.headers?.get?.("x-server-deployment") ?? null;

      if (correlationId) {
        recordStep({
          correlation_id: correlationId,
          step: "response_received",
          status: res.ok ? "ok" : "fail",
          code: String(res.status),
          duration_ms,
          detail: `${res.status} ${contentType ?? "no content-type"}`,
          metadata: {
            status: res.status,
            content_type: contentType,
            content_length: contentLength,
            deployment_id: deploymentId,
            empty_body: contentLength === "0",
          },
        });
      }

      if (!res.ok) {
        report({
          category,
          code: classifyHttpStatus(res.status),
          stage,
          request_id: requestId,
          correlation_id: correlationId ?? undefined,
          duration_ms,
          severity: res.status >= 500 ? "critical" : "error",
          wallet_impact: category === "wallet" || category === "bet" ? "unknown" : "not_applicable",
          metadata: {
            final_url: redactUrl(url),
            method,
            status: res.status,
            content_type: contentType,
            content_length: contentLength,
            deployment_id: deploymentId,
            online: typeof navigator !== "undefined" ? navigator.onLine : null,
          },
        });
      }
      return res;
    } catch (err) {
      const duration_ms = Date.now() - started;
      const code = classifyNetworkError(err);
      if (correlationId) {
        recordStep({
          correlation_id: correlationId,
          step: "response_received",
          status: "fail",
          code,
          duration_ms,
          detail: "no response reached the client",
        });
      }
      report({
        category: code === DIAG_CODES.offline || code === DIAG_CODES.network_failure ? "network" : category,
        code,
        stage,
        request_id: requestId,
        correlation_id: correlationId ?? undefined,
        duration_ms,
        error: err,
        severity: "error",
        wallet_impact: category === "wallet" || category === "bet" ? "unknown" : "not_applicable",
        metadata: {
          final_url: redactUrl(url),
          method,
          status: null,
          online: typeof navigator !== "undefined" ? navigator.onLine : null,
        },
      });
      throw err;
    }
  };

}

// ---------------------------------------------------------------------------
// Supabase / server-function call tracking
// ---------------------------------------------------------------------------

export type TrackOptions = {
  category: DiagCategory;
  stage: string;
  metric?: PerfMetricName;
  /** Only set for money paths; defaults to not_applicable. */
  walletImpactOnError?: "unknown" | "not_charged" | "charged";
  metadata?: Record<string, unknown>;
};

type SupabaseLike<T> = PromiseLike<{ data: T; error: unknown }>;

/**
 * Awaits a supabase-js builder, times it, and converts any PostgREST error
 * into a typed safe diagnostic. The original result is returned untouched so
 * existing call sites keep their behaviour.
 */
export async function trackSupabase<T>(
  q: SupabaseLike<T>,
  opts: TrackOptions,
): Promise<{ data: T; error: unknown; error_id?: string; safe_message?: string }> {
  const started = Date.now();
  try {
    const res = await q;
    const duration_ms = Date.now() - started;
    if (opts.metric) recordPerf(opts.metric, duration_ms, { metadata: opts.metadata });
    if (res?.error) {
      const { category, code } = classifySupabaseError(res.error);
      const { error_id, safe_message } = report({
        category: opts.category ?? category,
        code,
        stage: opts.stage,
        duration_ms,
        error: res.error,
        wallet_impact: opts.walletImpactOnError ?? "not_applicable",
        metadata: opts.metadata,
      });
      return { ...res, error_id, safe_message };
    }
    return res;
  } catch (err) {
    const duration_ms = Date.now() - started;
    if (opts.metric) recordPerf(opts.metric, duration_ms, { metadata: opts.metadata });
    const { category, code } = classifySupabaseError(err);
    const { error_id, safe_message } = report({
      category: opts.category ?? category,
      code,
      stage: opts.stage,
      duration_ms,
      error: err,
      wallet_impact: opts.walletImpactOnError ?? "not_applicable",
      metadata: opts.metadata,
    });
    return { data: null as unknown as T, error: err, error_id, safe_message };
  }
}

/**
 * Wraps a server-function (or any promise-returning) call so failures become
 * typed diagnostics with a user-showable error id. Rethrows nothing: callers
 * get a discriminated result.
 */
export async function trackCall<T>(
  fn: () => Promise<T>,
  opts: TrackOptions,
): Promise<
  | { ok: true; data: T; duration_ms: number }
  | { ok: false; error_id: string; safe_message: string; code: string; duration_ms: number }
> {
  const started = Date.now();
  try {
    const data = await fn();
    const duration_ms = Date.now() - started;
    if (opts.metric) recordPerf(opts.metric, duration_ms, { metadata: opts.metadata });
    return { ok: true, data, duration_ms };
  } catch (err) {
    const duration_ms = Date.now() - started;
    if (opts.metric) recordPerf(opts.metric, duration_ms, { metadata: opts.metadata });
    const code = classifyNetworkError(err);
    const { error_id, safe_message } = report({
      category: opts.category,
      code,
      stage: opts.stage,
      duration_ms,
      error: err,
      wallet_impact: opts.walletImpactOnError ?? "not_applicable",
      metadata: opts.metadata,
    });
    return { ok: false, error_id, safe_message, code, duration_ms };
  }
}
